import {
    CognitoUserPool,
    CognitoUser,
    AuthenticationDetails,
    CognitoUserAttribute
} from 'amazon-cognito-identity-js';
import awsConfig from '../config/aws-config';

const poolData = {
    UserPoolId: awsConfig.cognito.USER_POOL_ID,
    ClientId: awsConfig.cognito.APP_CLIENT_ID,
};

const userPool = new CognitoUserPool(poolData);

// Helper to generate UUID for username
function generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        var r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

class AWSAuthService {
    /**
     * Get the current authenticated user
     */
    public getCurrentUser(): CognitoUser | null {
        return userPool.getCurrentUser();
    }

    /**
     * Get user session
     */
    public getSession(): Promise<any> {
        const user = this.getCurrentUser();
        if (!user) return Promise.reject('No user logged in');

        return new Promise((resolve, reject) => {
            user.getSession((err: any, session: any) => {
                if (err) reject(err);
                else resolve(session);
            });
        });
    }

    /**
     * Sign In
     */
    public signIn(email: string, password: string): Promise<any> {
        const normalizedEmail = email.trim().toLowerCase();
        console.log(`AWS SignIn: Attempting for username/email: ${normalizedEmail}`);

        const authData = {
            Username: normalizedEmail,
            Password: password,
        };
        const authDetails = new AuthenticationDetails(authData);
        const userData = {
            Username: normalizedEmail,
            Pool: userPool,
        };
        const cognitoUser = new CognitoUser(userData);
        // Force non-SRP standard auth flow which we verified handles aliases perfectly
        cognitoUser.setAuthenticationFlowType('USER_PASSWORD_AUTH');

        return new Promise((resolve, reject) => {
            cognitoUser.authenticateUser(authDetails, {
                onSuccess: (result) => {
                    // Store user info in localStorage for fallback (like SyncService expects)
                    localStorage.setItem('authUser', JSON.stringify({
                        uid: result.getAccessToken().payload.sub,
                        email: normalizedEmail,
                        displayName: normalizedEmail.split('@')[0], // Fallback
                    }));
                    resolve(result);
                },
                onFailure: (err) => {
                    console.error('AWS SignIn Error:', err);
                    reject(err);
                },
                newPasswordRequired: (userAttributes) => {
                    // Handle new password required flow
                    resolve({ newPasswordRequired: true, userAttributes, user: cognitoUser });
                },
            });
        });
    }

    /**
     * Complete New Password Challenge
     */
    public completeNewPassword(cognitoUser: CognitoUser, newPassword: string, userAttributes: any): Promise<any> {
        return new Promise((resolve, reject) => {
            cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
                onSuccess: (result) => {
                    // Store user info in localStorage for fallback (like SyncService expects)
                    localStorage.setItem('authUser', JSON.stringify({
                        uid: result.getAccessToken().payload.sub,
                        email: cognitoUser.getUsername(),
                        displayName: cognitoUser.getUsername().split('@')[0], // Fallback
                    }));
                    resolve(result);
                },
                onFailure: (err) => reject(err),
            });
        });
    }

    /**
     * Sign Up
     */
    public signUp(email: string, password: string, attributes: any[] = []): Promise<any> {
        const normalizedEmail = email.trim().toLowerCase();

        // Since User Pool is configured with email alias, username cannot be email format.
        // We generate a UUID for the username, and ensure email is passed as an attribute.
        const username = generateUUID();

        // Ensure email is in attributes
        const emailAttr = attributes.find(a => a.Name === 'email');
        if (!emailAttr) {
            attributes.push({ Name: 'email', Value: normalizedEmail });
        } else {
            // Ensure the attribute value is also normalized
            emailAttr.Value = normalizedEmail;
        }

        const attributeList = attributes.map(attr => new CognitoUserAttribute(attr));

        return new Promise((resolve, reject) => {
            userPool.signUp(username, password, attributeList, [], (err: any, result) => {
                if (err) {
                    // Check if error is due to email already existing
                    // Cognito throws AliasExistsException when email alias is already in use
                    if (err.code === 'AliasExistsException' ||
                        err.message?.toLowerCase().includes('alias') ||
                        err.message?.toLowerCase().includes('already exists')) {
                        console.error('Duplicate email attempt:', normalizedEmail);
                        const duplicateError = new Error('An account with this email address already exists. Please use a different email or try logging in.') as Error & { code: string; name: string };
                        duplicateError.code = 'UsernameExistsException';
                        duplicateError.name = 'UsernameExistsException';
                        reject(duplicateError);
                    } else {
                        reject(err);
                    }
                } else {
                    resolve(result);
                }
            });
        });
    }

    /**
     * Sign Out
     */
    public signOut(): void {
        const user = this.getCurrentUser();
        if (user) {
            user.signOut();
        }
        localStorage.removeItem('authUser');
    }

    /**
     * Confirm Registration
     */
    public confirmRegistration(username: string, code: string): Promise<any> {
        const normalizedUsername = username.trim().toLowerCase();
        const userData = {
            Username: normalizedUsername,
            Pool: userPool,
        };
        const cognitoUser = new CognitoUser(userData);

        return new Promise((resolve, reject) => {
            cognitoUser.confirmRegistration(code, true, (err, result) => {
                if (err) reject(err);
                else resolve(result);
            });
        });
    }

    /**
     * Resend Confirmation Code
     */
    public resendConfirmationCode(username: string): Promise<any> {
        const normalizedUsername = username.trim().toLowerCase();
        const userData = {
            Username: normalizedUsername,
            Pool: userPool,
        };
        const cognitoUser = new CognitoUser(userData);

        return new Promise((resolve, reject) => {
            cognitoUser.resendConfirmationCode((err, result) => {
                if (err) reject(err);
                else resolve(result);
            });
        });
    }

    /**
     * Forgot Password
     */
    public forgotPassword(email: string): Promise<any> {
        const normalizedEmail = email.trim().toLowerCase();
        const userData = {
            Username: normalizedEmail,
            Pool: userPool,
        };
        const cognitoUser = new CognitoUser(userData);

        return new Promise((resolve, reject) => {
            cognitoUser.forgotPassword({
                onSuccess: (result) => resolve(result),
                onFailure: (err) => reject(err),
            });
        });
    }

    /**
     * Confirm Password (Reset)
     */
    public confirmPassword(email: string, code: string, newPass: string): Promise<any> {
        const normalizedEmail = email.trim().toLowerCase();
        const userData = {
            Username: normalizedEmail,
            Pool: userPool,
        };
        const cognitoUser = new CognitoUser(userData);

        return new Promise((resolve, reject) => {
            cognitoUser.confirmPassword(code, newPass, {
                onSuccess: () => resolve('Success'),
                onFailure: (err) => reject(err),
            });
        });
    }
}

const awsAuthService = new AWSAuthService();
export default awsAuthService;
